import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateTakeHomePay } from '../utils/taxCalculations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Using pound symbol and icons for UK currency
const { FiPieChart, FiPercent, FiMapPin, FiBriefcase } = FiIcons;

const TaxCalculator = ({ initialSalary = '' }) => {
  const [salary, setSalary] = useState(initialSalary.toString());
  const [region, setRegion] = useState('england');
  const [pensionPercent, setPensionPercent] = useState('5');
  const [studentLoanPlan, setStudentLoanPlan] = useState('none');
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (salary && parseFloat(salary) > 0) {
      const result = calculateTakeHomePay({
        annualSalary: parseFloat(salary),
        region,
        pensionPercent: parseFloat(pensionPercent) || 0,
        studentLoanPlan
      });
      setResults(result);
    } else {
      setResults(null);
    }
  }, [salary, region, pensionPercent, studentLoanPlan]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.section 
      className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-slate-200"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="grid lg:grid-cols-12">
        {/* Input Form */}
        <div className="lg:col-span-5 p-6 md:p-10 bg-slate-50/50 border-r border-slate-100">
          <div className="space-y-8">
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wider">
                <span className="text-blue-600 text-lg font-bold">£</span>
                Your Annual Salary
              </label>
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-bold text-slate-400">£</span>
                <input
                  type="number"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  placeholder="40,000"
                  className="w-full pl-10 pr-6 py-5 bg-white border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-600 outline-none text-2xl font-black text-slate-900 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wider">
                <SafeIcon icon={FiMapPin} className="text-indigo-600" />
                Tax Region
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setRegion('england')}
                  className={`px-4 py-4 rounded-xl border-2 font-bold transition-all ${
                    region === 'england'
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  UK (excl. Scot)
                </button>
                <button
                  onClick={() => setRegion('scotland')}
                  className={`px-4 py-4 rounded-xl border-2 font-bold transition-all ${
                    region === 'scotland'
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  Scotland
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wider">
                  <SafeIcon icon={FiPercent} className="text-emerald-600" />
                  Pension
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={pensionPercent}
                    onChange={(e) => setPensionPercent(e.target.value)}
                    min="0"
                    max="100"
                    className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-emerald-500 outline-none font-bold text-slate-900 transition-all"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">%</span>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wider">
                  <SafeIcon icon={FiBriefcase} className="text-amber-600" />
                  Student Loan
                </label>
                <select
                  value={studentLoanPlan}
                  onChange={(e) => setStudentLoanPlan(e.target.value)}
                  className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-amber-500 outline-none font-bold text-slate-900 transition-all appearance-none"
                >
                  <option value="none">None</option>
                  <option value="plan1">Plan 1</option>
                  <option value="plan2">Plan 2</option>
                  <option value="plan4">Plan 4</option>
                  <option value="plan5">Plan 5</option>
                  <option value="postgraduate">Postgrad</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Visuals */}
        <div className="lg:col-span-7 p-6 md:p-10 bg-white">
          <AnimatePresence mode="wait">
            {results ? (
              <motion.div 
                key="results"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="h-full flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-black text-slate-900">Calculated Pay</h3>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-black uppercase tracking-widest">2025/26 Rate</span>
                  </div>
                </div>

                {/* Main Take Home Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                  <div className="bg-emerald-600 p-6 rounded-3xl text-white shadow-xl shadow-emerald-200">
                    <div className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Monthly Net</div>
                    <div className="text-3xl font-black">{formatCurrency(results.netPay.monthly)}</div>
                  </div>
                  <div className="bg-slate-900 p-6 rounded-3xl text-white shadow-xl shadow-slate-200">
                    <div className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Yearly Net</div>
                    <div className="text-2xl font-black">{formatCurrency(results.netPay.annual)}</div>
                  </div>
                  <div className="bg-white border-2 border-slate-100 p-6 rounded-3xl text-slate-900 shadow-lg">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Weekly Net</div>
                    <div className="text-2xl font-black">{formatCurrency(results.netPay.weekly)}</div>
                  </div>
                </div>

                {/* Detailed Breakdown */}
                <div className="flex-grow space-y-4">
                  <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                    <SafeIcon icon={FiPieChart} />
                    Breakdown of Deductions
                  </h4>
                  
                  <div className="grid gap-3">
                    <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-rose-500" />
                        <span className="font-bold text-slate-700">Income Tax</span>
                      </div>
                      <span className="font-black text-rose-600">-{formatCurrency(results.incomeTax)}</span>
                    </div>

                    <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="font-bold text-slate-700">National Insurance</span>
                      </div>
                      <span className="font-black text-amber-600">-{formatCurrency(results.nationalInsurance)}</span>
                    </div>

                    {results.pensionContribution > 0 && (
                      <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-blue-500" />
                          <span className="font-bold text-slate-700">Pension ({pensionPercent}%)</span>
                        </div>
                        <span className="font-black text-blue-600">-{formatCurrency(results.pensionContribution)}</span>
                      </div>
                    )}

                    {results.studentLoan > 0 && (
                      <div className="flex justify-between items-center p-4 rounded-2xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-indigo-500" />
                          <span className="font-bold text-slate-700">Student Loan</span>
                        </div>
                        <span className="font-black text-indigo-600">-{formatCurrency(results.studentLoan)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-slate-500 font-bold">Total Deductions</span>
                  <span className="text-xl font-black text-slate-900">
                    {formatCurrency(results.incomeTax + results.nationalInsurance + results.pensionContribution + results.studentLoan)}
                  </span>
                </div>
              </motion.div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-slate-50/30 rounded-3xl border-2 border-dashed border-slate-200">
                <div className="w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center text-blue-600 mb-6">
                  <span className="text-5xl font-bold">£</span>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Ready to Calculate?</h3>
                <p className="text-slate-500 max-w-sm">Enter your annual salary to see a detailed breakdown of your 2025/26 take-home pay.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.section>
  );
};

export default TaxCalculator;